<template>
  <div class="bg-beige1 md:flex md:justify-center md:gap-x-4 md:px-4">
    <!-- 幅は中身ではなく basis で決める。新動線では畳んだセクションが display:none で幅に効かず、開いているセクションの分まで細る -->
    <div class="flex max-w-[700px] flex-col gap-y-4 font-Spectral md:basis-[700px] md:py-8">
      <div class="bg-white py-8 md:hidden">
        <ReservationWizard class="mb-4" current-reservation-step-label="Details" :reservation-type="experienceEvent.reservationType" />
      </div>
      <div v-if="experienceEvent.id && variant !== 'pending'">
        <ReservationStep2Form
          :code-redemption="codeRedemption"
          :experience-event="experienceEvent"
          :is-accordion="variant === 'variant'"
          :on-click-back-button="onClickBackButton"
          :on-success="onSuccess"
          :payment-method="paymentMethod"
          :pre-reservation-price="preReservationPrice"
          :pre-reservation-price-with-guest-type="preReservationPriceWithGuestType"
        />
      </div>
      <div v-else>
        <AtomsLoading class="m-auto h-full w-full" data-testid="reservation-loading" />
      </div>
    </div>
    <!-- PCのみ表示：体験タイトルと画像表示 + 予約情報表示セクション -->
    <!-- 右側の固定 -->
    <div class="sticky top-[102px] mt-8 hidden h-max w-1/3 min-w-[280px] max-w-[360px] bg-white px-4 py-8 md:block">
      <ReservationWizard class="mb-4" current-reservation-step-label="Details" :reservation-type="experienceEvent.reservationType" />
      <SummarySection
        :experience-event="experienceEvent"
        :is-done-reservation="false"
        :on-click-image="onClickBackButton"
        :pre-reservation-price="preReservationPrice"
        :pre-reservation-price-with-guest-type="preReservationPriceWithGuestType"
        :reservation-step1-form="reservationStep1Form"
        :reservation-step2-form="reservationStep2Form"
        :slug="slug"
      >
        <template v-if="variant === 'variant'" #beforeTotal>
          <ReservationCodeDisclosure :redemption="codeRedemption" />
        </template>
      </SummarySection>
    </div>
    <!-- 新動線では S2 を畳むと総額が見えなくなるため、モバイルは下部に固定で出す。PC は右カラムがそのまま残る（D-014） -->
    <ReservationPriceBar
      v-if="shouldShowPriceBar"
      :applied-code="reservationStep2Form.code"
      :code-redemption="codeRedemption"
      :experience-event="experienceEvent"
      :pre-reservation-price-with-guest-type="preReservationPriceWithGuestType"
    />
  </div>
</template>

<script setup lang="ts">
import { useRuntimeConfigKey, type UseRuntimeConfig } from '@/injectionKeys';
import { useAbVariant } from '@/wabunka-src/common/composables/general/useAbVariant/useAbVariant';
import { useReservationCodeRedemption } from '@/wabunka-src/common/features/Reservation/composables/forms/useReservationCodeRedemption';
import type { PaymentMethod } from '@/wabunka-src/constants/paymentMethod';
import type { WabunkaReservationFormStep1, WabunkaReservationFormStep2, ExperiencePreReservationPriceViewModel } from '@/wabunka-src/features/Experience';
import { ReservationWizard, SummarySection, ReservationStep2Form } from '@/wabunka-src/features/Experience';
import type { WabunkaExperienceEventViewModel } from '@/wabunka-src/features/Experience/models/experienceEvent';
import type { ExperiencePreReservationPriceWithGuestTypeViewModel } from '@/wabunka-src/features/Experience/subFeatures/Reservation/models/wabunkaExperiencePreReservationPrice';
import ReservationCodeDisclosure from '@/wabunka-src/features/Experience/subFeatures/Reservation/pages/Reserve/components/ReservationSection/ReservationCodeDisclosure/ReservationCodeDisclosure.vue';
import ReservationPriceBar from '@/wabunka-src/features/Experience/subFeatures/Reservation/pages/Reserve/components/ReservationSection/ReservationPriceBar/ReservationPriceBar.vue';

const props = defineProps<{
  slug: string;
  experienceEvent: WabunkaExperienceEventViewModel;
  reservationStep1Form: WabunkaReservationFormStep1;
  reservationStep2Form: WabunkaReservationFormStep2;
  preReservationPrice: ExperiencePreReservationPriceViewModel;
  preReservationPriceWithGuestType?: ExperiencePreReservationPriceWithGuestTypeViewModel;
  onClickBackButton: () => void;
  onSuccess: () => void;
  paymentMethod: PaymentMethod;
}>();

/** 割当待ちの上限。体験イベント読込中のローディングに重なる長さに抑える（D-013） */
const AB_VARIANT_TIMEOUT_MS = 1500;

// 新動線（アコーディオン分割）の割当。実験 ID が空のあいだは常に control で、誰にも配信されない。
// 割当が確定するまで Step2 フォームを描画しないのは、決済フォームの DOM を作り直さずに済ませるため（D-025）。
const useRuntimeConfig = inject(useRuntimeConfigKey) as UseRuntimeConfig;
const config = useRuntimeConfig();
const { variant } = useAbVariant((config.public.OPTIMIZE_NEXT_BOOKING_FORM_EXPERIENCE_ID ?? '') as string, { timeoutMs: AB_VARIANT_TIMEOUT_MS });

// 新動線の特典コードは Payment と内訳（モバイルのドロワー / PC の右カラム）の 3 か所から同じ状態を操作する（D-031）
const codeRedemption = useReservationCodeRedemption(() => props.preReservationPriceWithGuestType);

// 料金非公開の体験は右カラムにも金額を出していない
const shouldShowPriceBar = computed(() => variant.value === 'variant' && props.experienceEvent.experience.isPublicPrice);
</script>
